export const APP_TAB_TITLE = "Woods AI";

const MAX_TAB_SUBJECT_LENGTH = 70;

type RouteTitle = {
  pattern: RegExp;
  title: string;
};

const STATIC_ROUTE_TITLES: Record<string, string> = {
  "/": "Chat",
  "/agents": "Agents",
  "/agents/requests": "Agent requests",
  "/announcements": "Announcements",
  "/widgets": "Widgets",
  "/widgets/requests": "Widget share requests",
  "/chats": "Chats",
  "/files/requests": "File move requests",
  "/layouts": "Layouts",
  "/meeting-minutes": "Meeting minutes",
  "/meetings": "Meetings",
  "/meetings/admin": "Meeting Minutes Admin",
  "/models": "AI Models",
  "/notifications": "Notifications",
  "/onboarding": "Welcome",
  "/onboarding/resume": "Resume setup",
  "/onboarding/writing-style": "Writing style",
  "/projects": "Projects",
  "/projects/new": "Create a project",
  "/prompts": "Prompt Library",
  "/roadmap": "Roadmap",
  "/teams": "Teams",
  "/workflows": "Workflows",
  "/workflows/environment": "Environments",
  "/workflows/managed-agents": "Managed Agents",
  "/workflows/sessions": "Sessions",
  "/workflows/triggers": "Triggers",
};

const DYNAMIC_ROUTE_TITLES: RouteTitle[] = [
  { pattern: /^\/agents\/new\/edit$/, title: "Create agent" },
  { pattern: /^\/agents\/[^/]+\/edit$/, title: "Edit agent" },
  { pattern: /^\/agents\/[^/]+\/view$/, title: "Agent" },
  { pattern: /^\/announcements\/[^/]+$/, title: "Announcement" },
  { pattern: /^\/widgets\/[^/]+$/, title: "Widget" },
  { pattern: /^\/layouts\/new\/edit$/, title: "Create layout" },
  { pattern: /^\/layouts\/[^/]+\/edit$/, title: "Edit layout" },
  { pattern: /^\/models\/[^/]+$/, title: "AI Model" },
  { pattern: /^\/projects\/[^/]+\/agents$/, title: "Project agents" },
  { pattern: /^\/projects\/[^/]+\/widgets$/, title: "Project widgets" },
  { pattern: /^\/projects\/[^/]+\/notes\/[^/]+$/, title: "Note" },
  { pattern: /^\/projects\/[^/]+\/notes$/, title: "Notes" },
  { pattern: /^\/projects\/[^/]+\/files$/, title: "Project files" },
  { pattern: /^\/projects\/[^/]+\/managed-agents\/[^/]+$/, title: "Managed agent" },
  { pattern: /^\/projects\/[^/]+\/managed-agents$/, title: "Managed agents" },
  { pattern: /^\/projects\/[^/]+\/meeting-recordings$/, title: "Meeting minutes" },
  { pattern: /^\/projects\/[^/]+$/, title: "Project" },
  { pattern: /^\/prompts\/new\/edit$/, title: "Create prompt" },
  { pattern: /^\/prompts\/[^/]+\/edit$/, title: "Edit prompt" },
  { pattern: /^\/share\/[^/]+$/, title: "Shared chat" },
  { pattern: /^\/teams\/new\/edit$/, title: "New team" },
  { pattern: /^\/teams\/[^/]+\/edit$/, title: "Edit team" },
  { pattern: /^\/teams\/[^/]+$/, title: "Team" },
  { pattern: /^\/tools\/new\/edit$/, title: "Create tool" },
  { pattern: /^\/tools\/[^/]+\/edit$/, title: "Edit tool" },
  { pattern: /^\/workflows\/managed-agents\/[^/]+$/, title: "Managed agent" },
  { pattern: /^\/workflows\/sessions\/[^/]+$/, title: "Session" },
];

const normalizeTitlePart = (value: string) => value.replace(/\s+/g, " ").trim();

const truncateTitlePart = (value: string) => {
  const characters = Array.from(value);
  if (characters.length <= MAX_TAB_SUBJECT_LENGTH) {
    return value;
  }
  return `${characters.slice(0, MAX_TAB_SUBJECT_LENGTH - 1).join("")}…`;
};

export function formatTabTitle(subject?: string | null, status?: string | null): string {
  const normalizedSubject = normalizeTitlePart(subject ?? "");
  const normalizedStatus = normalizeTitlePart(status ?? "");

  if (!normalizedSubject || normalizedSubject === APP_TAB_TITLE) {
    return APP_TAB_TITLE;
  }

  const compactSubject = truncateTitlePart(normalizedSubject);
  const statusPrefix = normalizedStatus ? `[${normalizedStatus}] ` : "";
  return `${statusPrefix}${compactSubject} · ${APP_TAB_TITLE}`;
}

export function getRouteTabTitle(pathname: string): string {
  const normalizedPathname = pathname.length > 1 ? pathname.replace(/\/$/, "") : pathname;
  const staticTitle = STATIC_ROUTE_TITLES[normalizedPathname];
  if (staticTitle) {
    return staticTitle;
  }

  return (
    DYNAMIC_ROUTE_TITLES.find(({ pattern }) => pattern.test(normalizedPathname))?.title ??
    APP_TAB_TITLE
  );
}

export function getMeetingTabStatus(presentationLabel: string): string | null {
  switch (presentationLabel) {
    case "Generation stalled":
      return "Stalled";
    case "Ready to generate":
      return "Ready";
    case "Not started":
    case "Completed":
      return null;
    default:
      return presentationLabel;
  }
}
